import './PageAnnotations.css'
import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import formatMessage from 'format-message'
import classnames from 'classnames'
import fromPairs from 'lodash/fromPairs'
import throttle from 'lodash/throttle'
import isEqual from 'lodash/isEqual'
import InkAnnotation from './InkAnnotation'
import SquareAnnotation from './SquareAnnotation'
import HighlightAnnotation from './HighlightAnnotation'
import StrikeoutAnnotation from './StrikeoutAnnotation'
import PointAnnotation from './PointAnnotation'
import FreetextAnnotation from './freetext/FreetextAnnotation'
import {
  MARGIN_BETWEEN_COMMENT_GROUPS,
  SELECTION_BOX_PADDING,
  UNSTACKED_COMMENT_GROUP,
} from '../constants'
import { getMaxOffset, transformAnnotationView, translateAnnotation } from './utils'
import { splitRootAnnotationsAndReplies, compareRectYInverse } from '../utils'

const getCurrentCommentGroupHeights = (
  shouldCommentGroupStack,
  commentGroupHeights,
  stackedCommentGroupHeights,
  selectedAnnotationId,
) =>
  fromPairs(
    Object.keys(commentGroupHeights).map((annotationId) => {
      const isCommentGroupStacked =
        shouldCommentGroupStack[annotationId] === true && selectedAnnotationId !== annotationId

      return [
        annotationId,
        isCommentGroupStacked
          ? stackedCommentGroupHeights[annotationId]
          : commentGroupHeights[annotationId],
      ]
    }),
  )

class PageAnnotations_ extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      commentGroupHeights: {}, // by annotation id
      stackedCommentGroupHeights: {}, // by annotation id
      shouldCommentGroupStack: fromPairs(
        this.props.annotations.map((annotation) => [annotation.id, false]),
      ),
      hasError: false,
      managedCommentGroupTops: {},
    }

    this.repositionCommentGroups__throttled = throttle(this.repositionCommentGroups, 50)
  }

  componentDidUpdate(prevProps, prevState) {
    const didCommentGroupHeightsChange =
      prevState.commentGroupHeights !== this.state.commentGroupHeights

    const didManagedCommentGroupTopsChange =
      prevState.managedCommentGroupTops !== this.state.managedCommentGroupTops

    const stoppedDraggingAnnotation =
      prevProps.draggingAnnotationId != null && this.props.draggingAnnotationId == null

    const isNowCommenting = prevProps.isCommenting === false && this.props.isCommenting

    const selectedAnnotationChanged =
      prevProps.selectedAnnotationId !== this.props.selectedAnnotationId

    if (
      didCommentGroupHeightsChange ||
      didManagedCommentGroupTopsChange ||
      stoppedDraggingAnnotation ||
      isNowCommenting ||
      selectedAnnotationChanged
    ) {
      this.repositionCommentGroups__throttled()
    }

    const isThereNewlySelectedAnnotationOnThisPage =
      prevProps.selectedAnnotationId !== this.props.selectedAnnotationId &&
      this.props.annotations.some((annotation) => annotation.id === this.props.selectedAnnotationId)

    const newAnnotations = this.props.annotations.filter(
      (annotation) =>
        prevProps.annotations.some((prevAnnotation) => annotation.id === prevAnnotation.id) ===
        false,
    )

    if (newAnnotations.length > 0) {
      this.setState({
        shouldCommentGroupStack: {
          ...this.state.shouldCommentGroupStack,
          ...fromPairs(newAnnotations.map((annotation) => [annotation.id, false])),
        },
      })
    }

    const deletedAnnotations = prevProps.annotations.filter(
      (prevAnnotation) =>
        this.props.annotations.some((annotation) => annotation.id === prevAnnotation.id) === false,
    )

    const newAnnotationWasCreated =
      this.props.annotations.length > prevProps.annotations.length &&
      newAnnotations[0].type !== 'commentReply'

    if (
      isThereNewlySelectedAnnotationOnThisPage ||
      stoppedDraggingAnnotation ||
      isNowCommenting ||
      newAnnotationWasCreated
    ) {
      const getUpdatedScrollTop = () => {
        const transformWithViewport = transformAnnotationView({
          height: this.props.viewportHeight,
          scale: this.props.viewportScale,
        })

        const annotation = this.props.annotations.find(
          (annotation) => annotation.id === this.props.selectedAnnotationId,
        )

        if (annotation == null) {
          return
        }

        const selectedAnnotation = transformWithViewport(annotation)

        setTimeout(() => {
          this.props.changePagesContainerScrollTop(selectedAnnotation)
        }, 0)

        if (this.state.managedCommentGroupTops[this.props.selectedAnnotationId] == null) {
          return
        }
        return (
          this.state.managedCommentGroupTops[this.props.selectedAnnotationId] -
          selectedAnnotation.rect[1][1]
        )
      }

      // Because repositionCommentGroups__throttled sets the managed scrollTop asynchronously
      // we recalculate the scrollTop at different intervals and update when we need to
      // so that the comment is positioned correctly.
      const updateScrollTop = () => {
        let newScrollTop

        setTimeout(() => {
          const newScrollTop1 = getUpdatedScrollTop()

          if (newScrollTop1 !== newScrollTop) {
            newScrollTop = newScrollTop1
            this.props.changeScrollTop(newScrollTop)
          }
          setTimeout(() => {
            const newScrollTop2 = getUpdatedScrollTop()

            if (newScrollTop2 !== newScrollTop) {
              newScrollTop = newScrollTop2
              this.props.changeScrollTop(newScrollTop)
            }
            setTimeout(() => {
              const newScrollTop3 = getUpdatedScrollTop()

              if (newScrollTop3 !== newScrollTop) {
                newScrollTop = newScrollTop3
                this.props.changeScrollTop(newScrollTop)
              }
            }, 500)
          }, 200)
        }, 25)
      }

      updateScrollTop()
    }
  }

  componentDidCatch(error, info) {
    this.setState({ hasError: true }, () => {
      console.error('Error caught in PageAnnotations', { error, info })
    })
  }

  handleMoveAnnotation = (annotation, translateX, translateY) => {
    const updatedAnnotation = translateAnnotation(annotation, translateX, translateY)

    // If moving a multi-stroke annotation, end the multi-stroke session
    if (this.props.multiStrokeAnnotationId === annotation.id) {
      this.props.onMultiStrokeAnnotation(null)
      // Remove savedFromSession flag since we're saving to server
      delete updatedAnnotation.savedFromSession
    }

    this.props.onUpdateAnnotation(
      this.props.viewportHeight,
      this.props.viewportScale,
      updatedAnnotation,
      false, // No longer staged since we ended the multi-stroke session
    )
  }

  handleCommentGroupHeightChange = (annotationId, commentGroupHeight, commentGroupType) => {
    // We initialize both commentGroupHeightsCache and stackedCommentGroupHeightsCache
    // because we want them to have the same Object.keys(properties) length.
    // We use their property length to determine if we should perform a comment group reposition.
    if (this.props.commentGroupHeightsCache.current[annotationId] === undefined) {
      this.props.commentGroupHeightsCache.current[annotationId] = 0
    }

    if (this.props.stackedCommentGroupHeightsCache.current[annotationId] === undefined) {
      this.props.stackedCommentGroupHeightsCache.current[annotationId] = 0
    }

    const isUnstackedCommentGroup = commentGroupType === UNSTACKED_COMMENT_GROUP

    const clonedObj = isUnstackedCommentGroup
      ? Object.assign({}, this.props.commentGroupHeightsCache.current)
      : Object.assign({}, this.props.stackedCommentGroupHeightsCache.current)

    clonedObj[annotationId] = commentGroupHeight

    if (isUnstackedCommentGroup) {
      this.props.commentGroupHeightsCache.current = clonedObj
    } else {
      this.props.stackedCommentGroupHeightsCache.current = clonedObj
    }

    const [allCommentReplies, stagedRootAnnotations] = this.props.annotations.reduce(
      splitRootAnnotationsAndReplies,
      [[], []],
    )

    const annotationsWithReplies = stagedRootAnnotations.filter((annotation) =>
      allCommentReplies.some((reply) => annotation.id === reply.inreplyto),
    )

    const isReadyForReposition = Object.keys(clonedObj).length >= annotationsWithReplies.length

    if (isReadyForReposition) {
      this.props.setIsReadyForReposition(isReadyForReposition)
      this.setState(
        {
          commentGroupHeights: this.props.commentGroupHeightsCache.current,
          stackedCommentGroupHeights: this.props.stackedCommentGroupHeightsCache.current,
        },
        () => {
          this.repositionCommentGroups__throttled()

          const commentGroupHeights = getCurrentCommentGroupHeights(
            this.state.shouldCommentGroupStack,
            this.state.commentGroupHeights,
            this.state.stackedCommentGroupHeights,
            this.props.selectedAnnotationId,
          )

          this.props.onCommentGroupHeightsChange(commentGroupHeights)
        },
      )
    }
  }

  handleCommentGroupUnmount = (annotationId) => {
    const { [annotationId]: deletedCommentGroupHeight, ...updatedCommentGroupHeights } =
      this.state.commentGroupHeights

    const {
      [annotationId]: deletedStackedCommentGroupHeight,
      ...updatedStackedCommentGroupHeights
    } = this.state.stackedCommentGroupHeights

    const { [annotationId]: deletedCommentGroupTop, ...updatedCommentGroupTops } =
      this.state.managedCommentGroupTops

    this.props.commentGroupHeightsCache.current = updatedCommentGroupHeights

    this.setState(
      {
        commentGroupHeights: updatedCommentGroupHeights,
        managedCommentGroupTops: updatedCommentGroupTops,
        stackedCommentGroupHeights: updatedStackedCommentGroupHeights,
      },
      () => {
        const commentGroupHeights = getCurrentCommentGroupHeights(
          this.state.shouldCommentGroupStack,
          this.state.commentGroupHeights,
          this.state.stackedCommentGroupHeights,
          this.props.selectedAnnotationId,
        )
        this.props.onCommentGroupHeightsChange(commentGroupHeights)
      },
    )
  }

  repositionCommentGroups = () => {
    if (this.props.isVisible && this.props.isReadyForReposition === false) {
      return
    }

    const transformWithViewport = transformAnnotationView({
      height: this.props.viewportHeight,
      scale: this.props.viewportScale,
    })

    const [allCommentReplies, stagedRootAnnotations] = this.props.annotations
      .map(transformWithViewport)
      .reduce(splitRootAnnotationsAndReplies, [[], []])

    const isAnnotationFreetextAndDeleted = (annotation) => {
      return annotation.type === 'freetext' && annotation.deleted
    }
    const doesAnnotationHaveCommentGroup = (annotation) => {
      return (
        isAnnotationFreetextAndDeleted(annotation) ||
        (this.state.commentGroupHeights[annotation.id] != null &&
          (annotation.deleted ||
            this.props.selectedAnnotationId == annotation.id ||
            allCommentReplies.some((reply) => annotation.id === reply.inreplyto)))
      )
    }

    const annotationsWithCommentGroups = stagedRootAnnotations
      .filter(doesAnnotationHaveCommentGroup)
      .slice()
      .sort(compareRectYInverse)

    if (annotationsWithCommentGroups.length === 0) {
      return
    }

    const managedCommentGroupTops = {}
    const shouldCommentGroupStack = {}
    let bottomOfLast = 0 // start a max of greatest mismatch
    let previousAnnotation = null

    for (let annotation of annotationsWithCommentGroups) {
      shouldCommentGroupStack[annotation.id] = false

      let doesOverlapWithPrevious =
        annotation.rect[1][1] <= bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS

      if (doesOverlapWithPrevious && previousAnnotation !== null) {
        shouldCommentGroupStack[previousAnnotation.id] = true

        const hasStackedCommentGroupHeight =
          this.state.stackedCommentGroupHeights[previousAnnotation.id] !== undefined &&
          this.state.stackedCommentGroupHeights[previousAnnotation.id] !== 0
        const previousAnnotationIsNotSelected =
          this.props.selectedAnnotationId !== previousAnnotation.id

        if (hasStackedCommentGroupHeight && previousAnnotationIsNotSelected) {
          bottomOfLast -= this.state.commentGroupHeights[previousAnnotation.id]
          bottomOfLast += this.state.stackedCommentGroupHeights[previousAnnotation.id]
          doesOverlapWithPrevious =
            annotation.rect[1][1] <= bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS
        }
      }
      previousAnnotation = annotation

      const newTop = doesOverlapWithPrevious
        ? bottomOfLast + MARGIN_BETWEEN_COMMENT_GROUPS
        : annotation.rect[1][1]

      managedCommentGroupTops[annotation.id] = newTop

      bottomOfLast = newTop + this.state.commentGroupHeights[annotation.id]
    }

    const maxOffset = getMaxOffset(managedCommentGroupTops, annotationsWithCommentGroups)
    const managedCommentGroupTopsDidChange =
      isEqual(managedCommentGroupTops, this.state.managedCommentGroupTops) === false

    if (managedCommentGroupTopsDidChange) {
      this.props.onManagedCommentGroupTopsChange(managedCommentGroupTops)
      this.setState({ managedCommentGroupTops })
    }

    const maxOffsetDidChange = this.props.maxOffset !== maxOffset

    if (maxOffsetDidChange) {
      this.props.setMaxOffset(maxOffset)
      this.setState({ managedCommentGroupTops })
    }

    const shouldCommentGroupStackDidChange =
      isEqual(shouldCommentGroupStack, this.state.shouldCommentGroupStack) === false

    if (shouldCommentGroupStackDidChange) {
      this.setState({ shouldCommentGroupStack })
    }
  }

  render() {
    const transformWithViewport = transformAnnotationView({
      height: this.props.viewportHeight,
      scale: this.props.viewportScale,
    })

    const [allCommentReplies, stagedRootAnnotations] = this.props.annotations
      .map(transformWithViewport)
      .reduce(splitRootAnnotationsAndReplies, [[], []])

    const ySortedStagedRootAnnotations = stagedRootAnnotations.slice().sort(compareRectYInverse)

    const baseProps = {
      isAnnotatable: this.props.isAnnotatable,
      isShiftPressed: this.props.isShiftPressed,
      isVisible: this.props.isVisible,
      currentScrollTop: this.props.currentScrollTop,
      multiStrokeAnnotationId: this.props.multiStrokeAnnotationId,
      onCreateAnnotation: this.props.onCreateAnnotation,
      onCurrentBgColorChange: this.props.onCurrentBgColorChange,
      onCurrentColorChange: this.props.onCurrentColorChange,
      onCurrentInkStrokeWidthChange: this.props.onCurrentInkStrokeWidthChange,
      onCurrentTextSizeChange: this.props.onCurrentTextSizeChange,
      onDeleteAnnotation: this.props.onDeleteAnnotation,
      onDeselectAnnotation: this.props.onDeselectAnnotation,
      onDraggingEnd: this.props.handleDraggingEnd,
      onDraggingStart: this.props.handleDraggingStart,
      onHoverAnnotation: this.props.onHoverAnnotation,
      onIsCommentingChange: this.props.onIsCommentingChange,
      onIsPenAnnotatingChange: this.props.onIsPenAnnotatingChange,
      onSelectAnnotation: this.props.onSelectAnnotation,
      onBlockContextMenu: this.props.onBlockContextMenu,
      onTextSelectionDisable: this.props.onDisableTextSelection,
      onTextSelectionEnable: this.props.onEnableTextSelection,
      onUpdateAnnotation: this.props.onUpdateAnnotation,
      pageNumber: this.props.pageNumber,
      selectionBoxPadding: SELECTION_BOX_PADDING,
      sessionUserObject: this.props.sessionUserObject,
      viewportHeight: this.props.viewportHeight,
      viewportScale: this.props.viewportScale,
      viewportWidth: this.props.viewportWidth,
    }

    const renderedAnnotations = ySortedStagedRootAnnotations.map((annotation) => {
      const id = annotation.id

      const commentReplies = allCommentReplies
        .filter((textReply) => annotation.id === textReply.inreplyto)
        .sort(byCreatedAt)

      const annotationProps = {
        annotation,
        canEdit:
          this.props.sessionUserObject.user_id === annotation.user_id ||
          (annotation?.savedFromSession && this.props.isAnnotatable && !annotation.deleted),
        canDelete:
          (this.props.sessionUserObject.permissions.includes('manage') ||
            this.props.sessionUserObject.user_id === annotation.user_id ||
            annotation?.savedFromSession) &&
          this.props.isAnnotatable &&
          !annotation.deleted,
        commentsContainerRef: this.props.commentContainerRefs[annotation.id],
        shouldCommentGroupStack: this.state.shouldCommentGroupStack[annotation.id],
        commentReplies,
        controlMode: this.props.controlMode,
        handlePreviousPageFocus: this.props.handlePreviousPageFocus,
        handleNextPageFocus: this.props.handleNextPageFocus,
        isHovered: this.props.isDrawing === false && this.props.hoveredAnnotationId === id,
        isAutomaticallyScrollingComments: this.props.isAutomaticallyScrollingComments,
        isSelected: this.props.selectedAnnotationId === id,
        key: annotation.id,
        managedTop: this.state.managedCommentGroupTops[annotation.id],
        onCommentGroupHeightChange: this.handleCommentGroupHeightChange,
        onCommentGroupUnmount: this.handleCommentGroupUnmount,
      }

      const props = {
        ...baseProps,
        ...annotationProps,
        ySortedStagedRootAnnotations,
        pageHeight: this.props.pageHeight,
      }

      switch (annotation.type) {
        case 'square':
          return <SquareAnnotation {...props} onMoveAnnotation={this.handleMoveAnnotation} />
        case 'highlight':
          return <HighlightAnnotation {...props} />
        case 'strikeout':
          return <StrikeoutAnnotation {...props} />
        case 'text':
          return <PointAnnotation {...props} onMoveAnnotation={this.handleMoveAnnotation} />
        case 'freetext':
          return <FreetextAnnotation {...props} onMoveAnnotation={this.handleMoveAnnotation} />
        case 'ink':
          return (
            <InkAnnotation
              {...props}
              onMoveAnnotation={this.handleMoveAnnotation}
              onMultiStrokeAnnotation={this.props.onMultiStrokeAnnotation}
            />
          )
        default:
          return ''
      }
    })

    const annotationsClassnames = classnames('PageAnnotations', {
      'is-drawing': this.props.isDrawing,
      'is-dragging': this.props.draggingAnnotationId,
    })

    const bottomOffset = ReactDOM.createPortal(
      <div
        className="CommentContainerOffset"
        style={{
          top: `${this.props.pageHeight}px`,
          height: `${this.props.maxOffset}px`,
        }}
      />,
      this.props.commentContainerBottomOffsetRef.current,
    )

    return (
      <div className={annotationsClassnames}>
        {this.state.hasError && (
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: 'red',
              color: 'white',
            }}
          >
            {formatMessage('There was an error rendering annotations')}
          </div>
        )}

        {!this.state.hasError && renderedAnnotations}
        {bottomOffset}
      </div>
    )
  }
}

export default function PageAnnotations(props) {
  const commentGroupHeightsCache = useRef({})
  const stackedCommentGroupHeightsCache = useRef({})
  const [isReadyForReposition, setIsReadyForReposition] = useState(false)
  const [maxOffset, setMaxOffset] = useState(0)
  const [draggingAnnotationId, setDraggingAnnotationId] = useState('')

  const handleDraggingStart = (draggingAnnotationId) => {
    setDraggingAnnotationId(draggingAnnotationId)
  }

  const handleDraggingEnd = (draggingAnnotationId) => {
    setDraggingAnnotationId(draggingAnnotationId)
  }

  return (
    <PageAnnotations_
      {...props}
      isReadyForReposition={isReadyForReposition}
      setIsReadyForReposition={setIsReadyForReposition}
      maxOffset={maxOffset}
      setMaxOffset={setMaxOffset}
      draggingAnnotationId={draggingAnnotationId}
      setDraggingAnnotationId={setDraggingAnnotationId}
      commentGroupHeightsCache={commentGroupHeightsCache}
      stackedCommentGroupHeightsCache={stackedCommentGroupHeightsCache}
      handleDraggingStart={handleDraggingStart}
      handleDraggingEnd={handleDraggingEnd}
    />
  )
}

function byCreatedAt(a, b) {
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
}
